import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../../../../providers/AuthContext";
import { useNavigate } from "react-router-dom";
import { getWidgetById, getDataWidget } from "../../../../../services/apiWidgests";
import Spinner from "../../../../../components/Spinner";
import DatabaseOffIco from "../../../../../components/icons/DatabaseOffIco";
import { connectToProcess } from "../../../../../services/sockets/processSocket";
import { useDashboards } from "../../../../../providers/DashboardContext";
import { useAppContext } from '../../../../../providers/AppContext';

const StatusRegisterWidget = ({ id }) => {
  const { addToast } = useAppContext();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [config, setConfig] = useState(null);
  const {selectedDatarange} = useDashboards()
  const [fetchingWidgetData, setFetchingWidgetData] = useState(true);
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [initialOrder, setInitialOrder] = useState([]);
  const initialOrderRef = useRef([]);

  // 📥 Trae configuración del widget
  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      setConfig(response?.configuration || {});
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      console.error("Error cargando widget:", error);
      navigate("/logout");
    } finally {
      setFetchingWidgetInfo(false);
    }
  };

  // 📥 Trae datos del widget con spinner
  const refreshWidgetData = async () => {
    try {
      setFetchingWidgetData(true);
      const response = await getDataWidget(id, {}, auth?.token);
      const records = response?.records || [];
      setData(records);

      // Guardar orden inicial
      if (!initialOrderRef.current.length) {
        const order = records.map((item) => item.valorAgrupado);
        initialOrderRef.current = order;
        setInitialOrder(order);
      }
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      console.error("Error cargando datos:", error);
      navigate("/logout");
    } finally {
      setFetchingWidgetData(false);
    }
  };

  // 📥 Trae datos sin spinner, reordenando según initialOrder
  const refreshWidgetDataSilent = async () => {
    try {
      const response = await getDataWidget(id, {}, auth?.token);
      const records = response?.records || [];
      const ordered = initialOrderRef.current.length
        ? initialOrderRef.current
            .map((val) => records.find((r) => r.valorAgrupado === val))
            .filter(Boolean)
        : records;
      setData(ordered);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        if (typeof setFetchingWidgetData !== 'undefined') setFetchingWidgetData(false);
        if (typeof setFetchingWidgetInfo !== 'undefined') setFetchingWidgetInfo(false);
        return;
      }
      console.error("Error cargando datos:", error);
      navigate("/logout");
    }
  };

  // 🔹 Inicialización
  useEffect(() => {
    refreshWidgetInfo();
    refreshWidgetData();
  }, [selectedDatarange]);

  // 🔹 Actualización cada 5 segundos (Polling)
  useEffect(() => {
    if (!config?.process?.id) return;

    const interval = setInterval(() => {
      refreshWidgetDataSilent();
    }, 5000);

    return () => clearInterval(interval);
  }, [config?.process?.id, initialOrder]);


  // 🕒 Contador de tiempo
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // 🧩 Color según reglas
  const getColorByRules = (statusValue) => {
    const rules = config?.statusField?.rules || [];
    if (!rules.length) return "#6b7280";

    for (const rule of rules) {
      const { code, value, color } = rule;
      switch (code) {
        case "eq": if (statusValue === value) return color; break;
        case "ne": if (statusValue !== value) return color; break;
        case "gt": if (!isNaN(statusValue) && Number(statusValue) > Number(value)) return color; break;
        case "lt": if (!isNaN(statusValue) && Number(statusValue) < Number(value)) return color; break;
      }
    }
    return "#6b7280";
  };

  // ⏱️ Tiempo transcurrido
  const getElapsedTime = (lastTime) => {
    const diffMs = now - new Date(lastTime).getTime();
    if (diffMs < 0) return "0s";
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // 🔹 Render
  if (fetchingWidgetInfo || fetchingWidgetData) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <span className="text-gray-700">No hay datos por el momento</span>
        <DatabaseOffIco width="40px" height="40px" fill="gray" />
      </div>
    );
  }

  return (
    <div className="w-full h-full p-4 overflow-y-auto">
      <div className="w-full text-center border-b border-gray-300 pb-2 mb-4">
        <span className="font-semibold text-gray-700 text-lg">
          Estado por {config?.groupList?.alias || "grupo"}
        </span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-3">
        {data.map((item, idx) => {
          const color = getColorByRules(item.lastStatus);
          const elapsed = getElapsedTime(item.lastTime);
          const formattedTime = new Date(item.lastTime).toLocaleString("es-ES", {
            hour12: false,
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });

          return (
            <div
              key={idx}
              className="flex items-center p-3 bg-white shadow-sm rounded-xl border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 basis-1/12">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0 animate-pulse"
                  style={{ backgroundColor: color, boxShadow: `0 0 4px 1px ${color}` }}
                />
              </div>
              <div className="flex flex-col  items-center basis-11/12">
                <div className="flex w-full justify-between">
                  <span className="font-bold text-gray-800 truncate">{item.valorAgrupado}</span>
                  <span className="font-semibold text-gray-700 truncate">
                    {item.lastStatus} <span className="text-sm text-gray-400">({elapsed})</span>
                  </span>
                </div>
                <div className="flex w-full justify-between">
                  <span className="text-sm text-gray-500 truncate">{item.lastDetail}</span>

                </div>
                <div className="flex relative w-full justify-end">
                  <span className=" absolute text-xs  text-gray-500">{formattedTime}</span>
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatusRegisterWidget;
